import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  PageShell,
  SectionCard,
  Select,
  Skeleton,
  toast,
} from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import { useHasRole } from '@/providers/AuthProvider';
import { friendlyMessage } from '@/lib/errors';
import { PolicyToggleRow } from '../components/PolicyToggleRow';
import { useOrderPolicyQuery, useOrderPolicyMutation } from '../hooks/useOrderPolicy';
import { ORDER_POLICY_ROLES } from '../lib/orderPolicyRbac';
import {
  DEFAULT_ORDER_POLICY,
  STOCK_DECREMENT_TIMINGS,
  BENEFIT_GRANT_TIMINGS,
  type StockDecrementTiming,
  type BenefitGrantTiming,
  type OrderPolicySettings,
  type OrderPolicySettingsPayload,
} from '../types/orderPolicy';

/** 시점 select 한글 라벨(고도몰 표기). */
const STOCK_DECREMENT_LABELS: Record<StockDecrementTiming, string> = {
  ORDER: '주문 시 (입금대기 포함)',
  PAYMENT: '결제완료 시',
  DELIVERED: '배송완료 시',
};
const BENEFIT_GRANT_LABELS: Record<BenefitGrantTiming, string> = {
  PAYMENT: '결제완료 시',
  PURCHASE_CONFIRM: '구매확정 시',
  DELIVERED: '배송완료 시',
};

/** 숫자 입력 정규화 — 빈/비정상 입력은 1로 보정(BE 도 ≥1 클램프하지만 폼에서 선제 가드). */
const clampPositiveInt = (raw: string): number => {
  const n = Math.floor(Number(raw));
  return Number.isFinite(n) && n >= 1 ? n : 1;
};

const formatDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '-';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * 주문 기본설정 (고도몰 ID51_1, 01·02.png 정합). 26필드 단일 폼 —
 * 기존 6 + TASK-1 보강 신규 정책 11(ON/OFF·일수·시점 select) + 클레임 복원 9.
 * GET 로드 → 폼 시드, PUT 전체폼 저장(Idempotency-Key). RBAC=SUPER_ADMIN/OPS_ADMIN.
 * ⚠ 시점 enum·클레임 복원 9종은 정책 저장(표시)까지이며 실제 로직 적용은 후속 TASK(2/4/5).
 */
export const OrderPolicySettingsPage = () => {
  // ⚠ 본 페이지는 역사적으로 ko 하드코딩 — 신규/수정 문구만 t() 적용(점진 전환).
  const { t } = useTranslation('common');
  const canWrite = useHasRole(ORDER_POLICY_ROLES);
  const query = useOrderPolicyQuery();
  const mutation = useOrderPolicyMutation();

  // GET 결과를 폼 로컬 상태로 시드한다. 서버 도착 시 useEffect 로 리셋(편집 중이면
  // 사용자 입력 보존을 위해 dirty 가드 — 단, 최초 로드만 시드).
  const [form, setForm] = useState<OrderPolicySettings>(DEFAULT_ORDER_POLICY);
  const [seeded, setSeeded] = useState(false);

  useEffect(() => {
    if (!seeded && query.data) {
      setForm(query.data);
      setSeeded(true);
    }
  }, [seeded, query.data]);

  const update = <K extends keyof OrderPolicySettings>(
    key: K,
    value: OrderPolicySettings[K],
  ): void => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    // 전체폼 제출(26필드) — 기존 6 + 신규 정책 11 + 클레임 복원 9.
    const payload: OrderPolicySettingsPayload = {
      paymentDueHours: form.paymentDueHours,
      unpaidAutoCancelEnabled: form.unpaidAutoCancelEnabled,
      autoReadyOnPayment: form.autoReadyOnPayment,
      autoConfirmEnabled: form.autoConfirmEnabled,
      autoConfirmDays: form.autoConfirmDays,
      soldoutOrderBlock: form.soldoutOrderBlock,
      unpaidAutoCancelDays: form.unpaidAutoCancelDays,
      autoDeliveryCompleteEnabled: form.autoDeliveryCompleteEnabled,
      autoDeliveryCompleteDays: form.autoDeliveryCompleteDays,
      purchaseOfferReconfirm: form.purchaseOfferReconfirm,
      refundProgressReconfirm: form.refundProgressReconfirm,
      claimRequestEnabled: form.claimRequestEnabled,
      claimRequestOrderMark: form.claimRequestOrderMark,
      claimRequestVendorApply: form.claimRequestVendorApply,
      autoRefundEnabled: form.autoRefundEnabled,
      stockDecrementTiming: form.stockDecrementTiming,
      benefitGrantTiming: form.benefitGrantTiming,
      cancelRestoreStock: form.cancelRestoreStock,
      cancelRestoreCoupon: form.cancelRestoreCoupon,
      cancelGrantFreebie: form.cancelGrantFreebie,
      exchangeRestoreCoupon: form.exchangeRestoreCoupon,
      exchangeGrantFreebie: form.exchangeGrantFreebie,
      exchangeGrantMileage: form.exchangeGrantMileage,
      exchangeGrantCouponMileage: form.exchangeGrantCouponMileage,
      refundRestoreStock: form.refundRestoreStock,
      refundRestoreCoupon: form.refundRestoreCoupon,
    };
    mutation.mutate(payload, {
      onSuccess: (saved) => {
        setForm(saved);
        toast.success('주문 기본설정이 저장되었습니다.');
      },
      onError: (err) => toast.error(friendlyMessage(err, '저장에 실패했습니다.')),
    });
  };

  return (
    <PageShell
      title="주문 기본설정"
      breadcrumb={[{ label: '기본 설정' }, { label: '주문 기본설정' }]}
      description="결제 기한·자동 취소·자동 배송완료·자동 구매확정·청약/환불 재확인·교환반품환불 신청·자동환불·재고 차감/혜택 지급 시점 등 주문 처리 기본 정책을 설정합니다."
      data-testid="order-policy-page"
    >
      {query.isPending ? (
        <SectionCard title="결제·취소 정책">
          <div role="status" aria-live="polite" className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[40px] w-full" />
            ))}
          </div>
        </SectionCard>
      ) : query.isError ? (
        <SectionCard title="결제·취소 정책">
          <div
            role="alert"
            className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
            style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          >
            <p>주문 기본설정을 불러오지 못했습니다.</p>
            <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
              다시 시도
            </Button>
          </div>
        </SectionCard>
      ) : (
        <div className="space-y-6">
          <SectionCard
            title="결제·취소 정책"
            description="미결제 주문의 결제 기한과 기한 경과 시 자동 처리 방식을 설정합니다."
          >
            <div className="flex flex-col">
              <label
                htmlFor="payment-due-hours"
                className="text-sm font-medium text-[color:var(--content-strong)]"
              >
                결제 기한 (시간) <span className="text-[color:var(--content-muted)]">· 표시 전용</span>
              </label>
              <span className="mb-1.5 text-xs text-[color:var(--content-muted)]">
                미결제 주문이 유효한 시간. 현재 표시 전용이며 실제 만료 시점은 시스템 기본값을 따릅니다.
              </span>
              <Input
                id="payment-due-hours"
                type="number"
                min={1}
                inputMode="numeric"
                disabled
                className="w-40"
                value={form.paymentDueHours}
                onChange={(e) => update('paymentDueHours', clampPositiveInt(e.target.value))}
                data-testid="order-policy-payment-due-hours"
              />
            </div>

            <div className="mt-2 border-t" style={{ borderColor: 'var(--surface-1)' }}>
              <PolicyToggleRow
                label="결제 기한 경과 시 자동 취소"
                description="결제 기한이 지난 미결제 주문을 자동으로 취소합니다."
                checked={form.unpaidAutoCancelEnabled}
                disabled={!canWrite}
                onChange={(v) => update('unpaidAutoCancelEnabled', v)}
                testId="order-policy-unpaid-auto-cancel"
              />
            </div>
            <div
              className="flex flex-col border-t pt-3"
              style={{ borderColor: 'var(--surface-1)' }}
            >
              <label
                htmlFor="unpaid-auto-cancel-days"
                className="text-sm font-medium text-[color:var(--content-strong)]"
              >
                미입금 자동취소 기간 (일)
              </label>
              <span className="mb-1.5 text-xs text-[color:var(--content-muted)]">
                미입금 주문을 자동취소하기까지의 일수(고도몰 기본 3일). 자동 취소 사용 시 적용됩니다.
              </span>
              <Input
                id="unpaid-auto-cancel-days"
                type="number"
                min={1}
                inputMode="numeric"
                disabled={!canWrite || !form.unpaidAutoCancelEnabled}
                className="w-40"
                value={form.unpaidAutoCancelDays}
                onChange={(e) => update('unpaidAutoCancelDays', clampPositiveInt(e.target.value))}
                data-testid="order-policy-unpaid-auto-cancel-days"
              />
            </div>
          </SectionCard>

          <SectionCard
            title="배송·구매확정 정책"
            description="결제완료 후 상품준비중 전환과 배송완료 후 구매확정 처리를 설정합니다."
          >
            <PolicyToggleRow
              label="결제완료 시 자동 상품준비중 전환"
              description="결제가 완료되면 주문을 자동으로 상품준비중 상태로 전환합니다."
              checked={form.autoReadyOnPayment}
              disabled={!canWrite}
              onChange={(v) => update('autoReadyOnPayment', v)}
              testId="order-policy-auto-ready"
            />
            <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
              <PolicyToggleRow
                label="자동 구매확정 사용"
                description="배송완료 후 일정 기간이 지나면 자동으로 구매확정 처리합니다."
                checked={form.autoConfirmEnabled}
                disabled={!canWrite}
                onChange={(v) => update('autoConfirmEnabled', v)}
                testId="order-policy-auto-confirm"
              />
            </div>
            <div
              className="flex flex-col border-t pt-3"
              style={{ borderColor: 'var(--surface-1)' }}
            >
              <label
                htmlFor="auto-confirm-days"
                className="text-sm font-medium text-[color:var(--content-strong)]"
              >
                자동 구매확정 기간 (일)
              </label>
              <span className="mb-1.5 text-xs text-[color:var(--content-muted)]">
                배송완료 시점부터 자동 구매확정까지의 일수. 자동 구매확정 사용 시 적용됩니다.
              </span>
              <Input
                id="auto-confirm-days"
                type="number"
                min={1}
                inputMode="numeric"
                disabled={!canWrite || !form.autoConfirmEnabled}
                className="w-40"
                value={form.autoConfirmDays}
                onChange={(e) => update('autoConfirmDays', clampPositiveInt(e.target.value))}
                data-testid="order-policy-auto-confirm-days"
              />
            </div>
            <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
              <PolicyToggleRow
                label="자동 배송완료 사용"
                description="'배송중' 상태가 일정 기간 지나면 자동으로 '배송완료' 처리합니다."
                checked={form.autoDeliveryCompleteEnabled}
                disabled={!canWrite}
                onChange={(v) => update('autoDeliveryCompleteEnabled', v)}
                testId="order-policy-auto-delivery-complete"
              />
            </div>
            <div
              className="flex flex-col border-t pt-3"
              style={{ borderColor: 'var(--surface-1)' }}
            >
              <label
                htmlFor="auto-delivery-complete-days"
                className="text-sm font-medium text-[color:var(--content-strong)]"
              >
                자동 배송완료 기간 (일)
              </label>
              <span className="mb-1.5 text-xs text-[color:var(--content-muted)]">
                '배송중' 변경 시점부터 자동 배송완료까지의 일수(고도몰 기본 30일). 사용 시 적용됩니다.
              </span>
              <Input
                id="auto-delivery-complete-days"
                type="number"
                min={1}
                inputMode="numeric"
                disabled={!canWrite || !form.autoDeliveryCompleteEnabled}
                className="w-40"
                value={form.autoDeliveryCompleteDays}
                onChange={(e) =>
                  update('autoDeliveryCompleteDays', clampPositiveInt(e.target.value))
                }
                data-testid="order-policy-auto-delivery-complete-days"
              />
            </div>
          </SectionCard>

          <SectionCard
            title="주문 차단 정책"
            description="재고 상태에 따른 주문 가능 여부를 설정합니다."
          >
            <PolicyToggleRow
              label="품절 상품 주문 차단"
              description="품절된 상품에 대한 신규 주문을 차단합니다."
              checked={form.soldoutOrderBlock}
              disabled={!canWrite}
              onChange={(v) => update('soldoutOrderBlock', v)}
              testId="order-policy-soldout-block"
            />
          </SectionCard>

          <SectionCard
            title="청약·환불 확인 정책"
            description="결제 전 청약의사 재확인과 환불 진행 시 재확인 절차를 설정합니다."
          >
            <PolicyToggleRow
              label="결제페이지 청약의사 재확인 · SF 적용 예정"
              description="결제 단계에서 청약 의사를 다시 확인하는 절차입니다. 쇼핑몰 적용은 준비 중이며 현재는 설정만 저장됩니다."
              checked={form.purchaseOfferReconfirm}
              disabled
              onChange={(v) => update('purchaseOfferReconfirm', v)}
              testId="order-policy-purchase-offer-reconfirm"
            />
            <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
              <PolicyToggleRow
                label="환불 진행 재확인 · 미연동"
                description="환불 처리 시 2차 확인 절차입니다. 현재 실제 동작에는 연동되지 않으며 설정만 저장됩니다."
                checked={form.refundProgressReconfirm}
                disabled
                onChange={(v) => update('refundProgressReconfirm', v)}
                testId="order-policy-refund-progress-reconfirm"
              />
            </div>
          </SectionCard>

          <SectionCard
            title="교환·반품·환불 신청 정책"
            description="고객의 교환/반품/환불 직접 신청 허용과 자동환불 사용 여부를 설정합니다."
          >
            <PolicyToggleRow
              label="교환/반품/환불 신청 사용"
              description="고객이 쇼핑몰에서 직접 교환·반품·환불을 신청할 수 있도록 합니다."
              checked={form.claimRequestEnabled}
              disabled={!canWrite}
              onChange={(v) => update('claimRequestEnabled', v)}
              testId="order-policy-claim-request-enabled"
            />
            <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
              <PolicyToggleRow
                label="고객 신청 건 주문목록 표기"
                description="고객이 신청한 클레임 건을 주문 목록에 표기합니다."
                checked={form.claimRequestOrderMark}
                disabled={!canWrite || !form.claimRequestEnabled}
                onChange={(v) => update('claimRequestOrderMark', v)}
                testId="order-policy-claim-request-order-mark"
              />
            </div>
            <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
              <PolicyToggleRow
                label="공급사 관리자 동일 적용 · 공급사 연동 예정"
                description="공급사 관리자 화면에도 동일하게 신청 처리를 적용합니다. 공급사 연동은 준비 중이며 현재는 설정만 저장됩니다."
                checked={form.claimRequestVendorApply}
                disabled
                onChange={(v) => update('claimRequestVendorApply', v)}
                testId="order-policy-claim-request-vendor-apply"
              />
            </div>
            <div className="border-t" style={{ borderColor: 'var(--surface-1)' }}>
              <PolicyToggleRow
                label="자동환불 사용 · 운영자 승인 고정"
                description="모든 환불은 운영자 승인 후 처리됩니다. 현재 자동환불은 지원되지 않으며 설정만 저장됩니다."
                checked={form.autoRefundEnabled}
                disabled
                onChange={(v) => update('autoRefundEnabled', v)}
                testId="order-policy-auto-refund-enabled"
              />
            </div>
          </SectionCard>

          <SectionCard
            title="재고 차감·혜택 지급 시점"
            description="재고를 차감하는 시점과 마일리지·쿠폰 혜택을 지급하는 시점을 설정합니다. (실제 적용은 후속 단계에서 연동됩니다.)"
          >
            <div className="flex flex-col">
              <label
                htmlFor="stock-decrement-timing"
                className="text-sm font-medium text-[color:var(--content-strong)]"
              >
                재고 차감 시점 <span className="text-[color:var(--content-muted)]">· 현재 결제완료 시 고정</span>
              </label>
              <span className="mb-1.5 text-xs text-[color:var(--content-muted)]">
                현재 재고는 결제완료 시 차감되며, 시점 변경은 준비 중입니다(설정만 저장).
              </span>
              <Select
                id="stock-decrement-timing"
                disabled
                className="w-60"
                // 잠금(disabled) — 저장된 값을 그대로 노출, 실제 동작은 아래 안내문 참조.
                value={form.stockDecrementTiming}
                onChange={(e) =>
                  update('stockDecrementTiming', e.target.value as StockDecrementTiming)
                }
                data-testid="order-policy-stock-decrement-timing"
              >
                {STOCK_DECREMENT_TIMINGS.map((opt) => (
                  <option key={opt} value={opt}>
                    {STOCK_DECREMENT_LABELS[opt]}
                  </option>
                ))}
              </Select>
            </div>
            <div
              className="mt-3 flex flex-col border-t pt-3"
              style={{ borderColor: 'var(--surface-1)' }}
            >
              <label
                htmlFor="benefit-grant-timing"
                className="text-sm font-medium text-[color:var(--content-strong)]"
              >
                혜택 지급 시점 <span className="text-[color:var(--content-muted)]">· 현재 구매확정 시 고정</span>
              </label>
              <span className="mb-1.5 text-xs text-[color:var(--content-muted)]">
                현재 마일리지·쿠폰 혜택은 구매확정 시 지급되며, 시점 변경은 준비 중입니다(설정만 저장).
              </span>
              <Select
                id="benefit-grant-timing"
                disabled
                className="w-60"
                // 잠금(disabled) — 저장된 값을 그대로 노출, 실제 동작은 아래 안내문 참조.
                value={form.benefitGrantTiming}
                onChange={(e) =>
                  update('benefitGrantTiming', e.target.value as BenefitGrantTiming)
                }
                data-testid="order-policy-benefit-grant-timing"
              >
                {BENEFIT_GRANT_TIMINGS.map((opt) => (
                  <option key={opt} value={opt}>
                    {BENEFIT_GRANT_LABELS[opt]}
                  </option>
                ))}
              </Select>
            </div>
          </SectionCard>

          {/* [coupon-policy] 쿠폰 복원 3토글(주문취소/교환접수/환불완료)은 전부 LIVE —
              주문취소=canceled.v1 restoreCoupon, 교환/환불=claim.completed.v1 per-claim-type
              restoreCoupon(부분환불 포함, be-snapshot 푸시 완료) → user-service 컨슈머가
              주문 스코프 복원. 재고/사은품/마일리지 항목은 신호 동봉(소비 연동 후속). */}
          <SectionCard
            title="클레임 처리 기본 노출 설정"
            description={t(
              'orderPolicy.claimRestoreSectionDesc',
              '취소·교환·환불 처리 시 재고/쿠폰/사은품/마일리지 복원·지급의 기본 선택값을 설정합니다. "쿠폰 복원" 토글(주문취소·교환접수·환불완료)은 즉시 적용됩니다 — 부분환불 포함. 사은품·마일리지 항목은 클레임 화면 기본값으로 노출되고 자동 적용은 순차 연동됩니다.',
            )}
          >
            <div className="space-y-1">
              <h4 className="text-sm font-semibold text-[color:var(--content-strong)]">
                주문취소 시
              </h4>
              <PolicyToggleRow
                label="재고 수량 복원"
                checked={form.cancelRestoreStock}
                disabled={!canWrite}
                onChange={(v) => update('cancelRestoreStock', v)}
                testId="order-policy-cancel-restore-stock"
              />
              <PolicyToggleRow
                label="쿠폰 복원"
                description={t(
                  'orderPolicy.cancelRestoreCouponDesc',
                  '켜면 주문 전체취소 시 사용한 쿠폰을 자동 복원합니다(즉시 적용). 끔(기본) = 복원 안 함.',
                )}
                checked={form.cancelRestoreCoupon}
                disabled={!canWrite}
                onChange={(v) => update('cancelRestoreCoupon', v)}
                testId="order-policy-cancel-restore-coupon"
              />
              <PolicyToggleRow
                label="사은품 지급 · 미연동"
                description="현재 실제 동작에는 연동되지 않으며 설정만 저장됩니다."
                checked={form.cancelGrantFreebie}
                disabled
                onChange={(v) => update('cancelGrantFreebie', v)}
                testId="order-policy-cancel-grant-freebie"
              />
            </div>
            <div
              className="mt-3 space-y-1 border-t pt-3"
              style={{ borderColor: 'var(--surface-1)' }}
            >
              <h4 className="text-sm font-semibold text-[color:var(--content-strong)]">
                교환접수 시
              </h4>
              <PolicyToggleRow
                label="교환취소 상품 쿠폰 복원"
                description={t(
                  'orderPolicy.exchangeRestoreCouponDesc',
                  '켜면 교환접수 클레임 완료 시 교환취소 상품의 사용 쿠폰을 자동 복원합니다(즉시 적용).',
                )}
                checked={form.exchangeRestoreCoupon}
                disabled={!canWrite}
                onChange={(v) => update('exchangeRestoreCoupon', v)}
                testId="order-policy-exchange-restore-coupon"
              />
              <PolicyToggleRow
                label="교환취소 상품 사은품 지급 · 미연동"
                description="현재 실제 동작에는 연동되지 않으며 설정만 저장됩니다."
                checked={form.exchangeGrantFreebie}
                disabled
                onChange={(v) => update('exchangeGrantFreebie', v)}
                testId="order-policy-exchange-grant-freebie"
              />
              <PolicyToggleRow
                label="교환추가 상품 마일리지 지급 · 미연동"
                description="현재 실제 동작에는 연동되지 않으며 설정만 저장됩니다."
                checked={form.exchangeGrantMileage}
                disabled
                onChange={(v) => update('exchangeGrantMileage', v)}
                testId="order-policy-exchange-grant-mileage"
              />
              <PolicyToggleRow
                label="교환추가 상품 쿠폰 마일리지 지급 · 미연동"
                description="현재 실제 동작에는 연동되지 않으며 설정만 저장됩니다."
                checked={form.exchangeGrantCouponMileage}
                disabled
                onChange={(v) => update('exchangeGrantCouponMileage', v)}
                testId="order-policy-exchange-grant-coupon-mileage"
              />
            </div>
            <div
              className="mt-3 space-y-1 border-t pt-3"
              style={{ borderColor: 'var(--surface-1)' }}
            >
              <h4 className="text-sm font-semibold text-[color:var(--content-strong)]">
                환불완료 시
              </h4>
              <PolicyToggleRow
                label="재고 수량 복원"
                checked={form.refundRestoreStock}
                disabled={!canWrite}
                onChange={(v) => update('refundRestoreStock', v)}
                testId="order-policy-refund-restore-stock"
              />
              <PolicyToggleRow
                label="쿠폰 복원"
                description={t(
                  'orderPolicy.refundRestoreCouponDesc',
                  '켜면 환불(부분환불 포함) 클레임 완료 시 사용 쿠폰을 자동 복원합니다(즉시 적용). 끔(기본) = 복원 안 함.',
                )}
                checked={form.refundRestoreCoupon}
                disabled={!canWrite}
                onChange={(v) => update('refundRestoreCoupon', v)}
                testId="order-policy-refund-restore-coupon"
              />
            </div>
          </SectionCard>

          <div
            className="flex flex-wrap items-center justify-between gap-3 rounded-xl p-4"
            style={{ background: 'var(--surface-raised)' }}
          >
            <span className="text-xs text-[color:var(--content-muted)]">
              {form.updatedBy || form.updatedAt
                ? `최종 수정: ${form.updatedBy ?? '-'} · ${formatDateTime(form.updatedAt)}`
                : '저장 이력이 없습니다.'}
            </span>
            <Button
              variant="cta"
              size="md"
              disabled={!canWrite || mutation.isPending}
              onClick={handleSave}
              data-testid="order-policy-save"
            >
              {mutation.isPending ? '저장 중...' : '저장'}
            </Button>
          </div>
        </div>
      )}
    </PageShell>
  );
};
